import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { setEmailError, setPasswordError } from "../slices/authAdminSlice";
import { AppDispatch } from "../store";

const LOGIN_URL = "https://dev.api.jomuntu.com/api/v1/users/login";
const CURRENT_USER_URL =
  "https://dev.api.jomuntu.com/api/v1/users/current-user";
const REFRESH_TOKEN_URL =
  "https://dev.api.jomuntu.com/api/v1/users/refresh-token";
const FORGOT_PASSWORD_URL =
  "https://dev.api.jomuntu.com/api/v1/users/forgot-password";
const RESET_PASSWORD_URL =
  "https://dev.api.jomuntu.com/api/v1/users/reset-password";

  export const isTokenExpired = (dispatch: AppDispatch) => {
    const expiration = localStorage.getItem("expiration");
    const isExpired = !expiration || Date.now() >= Number(expiration);
  
    if (isExpired) {
      // Attempt to refresh the token
      dispatch(refreshToken());
    }
  
    return isExpired;
  };

// Login action
export const login = createAsyncThunk(
  "auth/login",
  async (
    credentials: { email: string; password: string },
    { dispatch, rejectWithValue }
  ) => {
    const { email, password } = credentials;

    if (!email) {
      dispatch(setEmailError("Please enter your email."));
      return rejectWithValue(null);
    }

    if (!password) {
      dispatch(setPasswordError("Please enter your password."));
      return rejectWithValue(null);
    }

    try {
      const response = await axios.post(LOGIN_URL, credentials);
      const { accessToken, refreshToken, expiresIn } = response.data;

      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("expiration", String(Date.now() + expiresIn * 1000));

      dispatch(fetchCurrentUser());
      return accessToken;
    } catch (error) {
      return rejectWithValue("Invalid login credentials");
    }
  }
);

//Reset password
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    { newPassword, confirmNewPassword }: { newPassword: string; confirmNewPassword: string },
    { dispatch, rejectWithValue }
  ) => {
    const token = new URLSearchParams(window.location.search).get("token");

    if (!newPassword) {
      dispatch(setPasswordError("Please enter your password."));
      return rejectWithValue("Password is required.");
    }

    if (!confirmNewPassword) {
      dispatch(setPasswordError("Please enter your confirm password."));
      return rejectWithValue("Confirm Password is required.");
    }

    if (newPassword !== confirmNewPassword) {
      dispatch(setPasswordError("Passwords do not match."));
      return rejectWithValue("Passwords do not match.");
    }

    if (!token) {
      return rejectWithValue("Token is required.");
    }

    try {
      const response = await axios.put(
        `${RESET_PASSWORD_URL}?token=${token}`,
        { newPassword, confirmNewPassword }
      );

      return {
        ...response.data,
        token,
      };
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Failed to reset password.";
      return rejectWithValue(message);
    }
  }
);

// Forgot Password action
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (
    { email }: { email: string },
    { dispatch, rejectWithValue }
  ) => {
    if (!email) {
      dispatch(setEmailError("Please enter your email."));
      return rejectWithValue(null);
    }

    try {
      const response = await axios.post(FORGOT_PASSWORD_URL, { email });
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to send password reset email.");
    }
  }
);

// Refresh Token action
export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(REFRESH_TOKEN_URL, {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { accessToken, expiresIn } = response.data;

      // Store the new access token and update expiration time
      localStorage.setItem("token", accessToken);
      localStorage.setItem("expiration", String(Date.now() + expiresIn * 1000));

      return accessToken;
    } catch (error) {
      return rejectWithValue("Failed to refresh the token.");
    }
  }
);

// Fetch current user
export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(CURRENT_USER_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { name, roles } = response.data;
      const role = roles[0]?.name || "user";

      return { name, role };
    } catch (error) {
      return rejectWithValue("Failed to fetch current user");
    }
  }
);
